import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { type BackgroundJobType, type RequestBackgroundJobInput } from "~/gql/graphqlBase.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import {
	useCancelBackgroundJobMutation,
	useClearFinishedBackgroundJobsMutation,
	useDismissBackgroundJobMutation,
	useRequestBackgroundJobMutation,
	useRetryFailedBackgroundJobItemsMutation
} from "~/services/endpoints/apiEndpoints";

export interface UseBackgroundJobMutationsReturn {
	cancelBackgroundJob: (jobId: string) => Promise<boolean>;
	clearFinishedBackgroundJobs: () => Promise<boolean>;
	dismissBackgroundJob: (jobId: string, type?: BackgroundJobType | null) => Promise<boolean>;
	requestBackgroundJob: (
		input: RequestBackgroundJobInput,
		failureMessage?: string
	) => Promise<{ jobId: string } | null>;
	requestCreatePlanJob: (input: RequestBackgroundJobInput) => Promise<{ jobId: string } | null>;
	retryFailedBackgroundJobItems: (jobId: string) => Promise<boolean>;
}

const useBackgroundJobMutations = (): UseBackgroundJobMutationsReturn => {
	const { t } = useTranslation("common");
	const { run, runForEntity } = useMutationRunner();
	const [cancelMutation] = useCancelBackgroundJobMutation();
	const [retryMutation] = useRetryFailedBackgroundJobItemsMutation();
	const [dismissMutation] = useDismissBackgroundJobMutation();
	const [clearMutation] = useClearFinishedBackgroundJobsMutation();
	const [requestMutation] = useRequestBackgroundJobMutation();
	const errorMessage = t("backgroundJobs.actionFailed");
	const requestFailed = t("backgroundJobs.requestFailed");

	const cancelBackgroundJob = useCallback(
		(jobId: string) => run(cancelMutation({ jobId }), (data) => data.cancelBackgroundJob, errorMessage),
		[cancelMutation, errorMessage, run]
	);

	const retryFailedBackgroundJobItems = useCallback(
		(jobId: string) => run(retryMutation({ jobId }), (data) => data.retryFailedBackgroundJobItems, errorMessage),
		[errorMessage, retryMutation, run]
	);

	const dismissBackgroundJob = useCallback(
		(jobId: string, type?: BackgroundJobType | null) =>
			run(dismissMutation({ jobId, type }), (data) => data.dismissBackgroundJob, errorMessage),
		[dismissMutation, errorMessage, run]
	);

	const clearFinishedBackgroundJobs = useCallback(
		() => run(clearMutation(), (data) => data.clearFinishedBackgroundJobs, errorMessage),
		[clearMutation, errorMessage, run]
	);

	const requestBackgroundJob = useCallback(
		(input: RequestBackgroundJobInput, failureMessage?: string) =>
			runForEntity(
				requestMutation({ input }),
				(data) => data.requestBackgroundJob,
				(payload) => payload.backgroundJob ?? null,
				failureMessage ?? requestFailed
			),
		[requestFailed, requestMutation, runForEntity]
	);

	const requestCreatePlanJob = useCallback(
		(input: RequestBackgroundJobInput) => requestBackgroundJob(input, snackMessages.createPlanJobError()),
		[requestBackgroundJob]
	);

	return {
		cancelBackgroundJob,
		clearFinishedBackgroundJobs,
		dismissBackgroundJob,
		requestBackgroundJob,
		requestCreatePlanJob,
		retryFailedBackgroundJobItems
	};
};

export default useBackgroundJobMutations;
